import { BarChart3, BookOpen, Bug, Cloud, Cog, Globe2, HardDrive, HeartPulse, Inbox, KeyRound, ListOrdered, Mail, PenLine, Send, ShieldAlert, Ticket, Users } from 'lucide-react'
import { Navigate, Route, Routes } from 'react-router-dom'
import { AppShell } from './components/AppShell'
import type { NavEntry } from './components/AppShell'
import { HomePage } from './components/HomePage'
import { LoginForm } from './components/LoginForm'
import { RegisterForm } from './components/RegisterForm'
import { RequireAuth } from './components/RequireAuth'
import { SSOCallback } from './components/SSOCallback'
import { AdminDashboardPage } from './features/admin/DashboardPage'
import { BackupPage } from './features/admin/BackupPage'
import { DeployGuidePage } from './features/admin/DeployGuidePage'
import { DomainsPage } from './features/admin/DomainsPage'
import { InvitationsPage } from './features/admin/InvitationsPage'
import { LogsPage } from './features/admin/LogsPage'
import { MailboxRequestsPage } from './features/admin/MailboxRequestsPage'
import { QueuePage } from './features/admin/QueuePage'
import { AdminSettingsPage } from './features/admin/SettingsPage'
import { SpamPage } from './features/admin/SpamPage'
import { SSOPage } from './features/admin/SSOPage'
import { SystemHealthPage } from './features/admin/SystemHealthPage'
import { DNSPage } from './features/admin/DNSPage'
import { UsersPage } from './features/admin/UsersPage'
import { ComposePage } from './features/user/ComposePage'
import { ContactsPage } from './features/user/ContactsPage'
import { FolderPage } from './features/user/FolderPage'
import { MailboxPage } from './features/user/MailboxPage'
import { MailboxRequestPage } from './features/user/MailboxRequestPage'
import { MailLayout } from './features/user/MailLayout'
import { SentPage } from './features/user/SentPage'
import { UserSettingsPage } from './features/user/SettingsPage'
import { UserDashboardPage } from './features/user/DashboardPage'

const adminNav: NavEntry[] = [
  { to: '/admin/dashboard', icon: BarChart3, label: 'admin.dashboard' },
  { label: 'admin.groupMail', icon: Mail, items: [
    { to: '/admin/domains', icon: Globe2, label: 'admin.domains' },
    { to: '/admin/dns', icon: Cloud, label: 'admin.dnsManagement' },
    { to: '/admin/mailbox-requests', icon: Inbox, label: 'admin.mailboxRequests' },
    { to: '/admin/queue', icon: ListOrdered, label: 'admin.queue' },
    { to: '/admin/spam', icon: ShieldAlert, label: 'admin.spam' },
  ]},
  { label: 'admin.groupAccess', icon: Users, items: [
    { to: '/admin/users', icon: Users, label: 'admin.users' },
    { to: '/admin/invitations', icon: Ticket, label: 'admin.invitations' },
    { to: '/admin/sso', icon: KeyRound, label: 'admin.ssoSettings' },
  ]},
  { label: 'admin.groupSystem', icon: Cog, items: [
    { to: '/admin/system-health', icon: HeartPulse, label: 'admin.systemHealth' },
    { to: '/admin/backup', icon: HardDrive, label: 'admin.backup' },
    { to: '/admin/deploy-guide', icon: BookOpen, label: 'admin.deployGuide' },
    { to: '/admin/logs', icon: Bug, label: 'admin.logs' },
    { to: '/admin/settings', icon: Cog, label: 'admin.settings' },
  ]},
]
const userNav: NavEntry[] = [
  { to: '/user/dashboard', icon: BarChart3, label: 'user.dashboard' },
  { to: '/user/mailbox-request', icon: Mail, label: 'user.requestMailbox' },
  { to: '/user/mail', icon: Inbox, label: 'user.mail' },
  { to: '/user/compose', icon: PenLine, label: 'user.compose' },
  { to: '/user/contacts', icon: Users, label: 'user.contacts' },
  { to: '/user/settings', icon: Cog, label: 'user.settings' },
]

export function App() {
  return <Routes>
    <Route path="/" element={<HomePage />} />
    <Route path="/admin/login" element={<LoginForm mode="admin" />} />
    <Route path="/user/login" element={<LoginForm mode="user" />} />
    <Route path="/user/register" element={<RegisterForm />} />
    <Route path="/auth/callback" element={<SSOCallback />} />
    <Route path="/admin" element={<RequireAuth audience="admin"><AppShell audience="admin" items={adminNav} /></RequireAuth>}>
      <Route index element={<Navigate to="dashboard" replace />} />
      <Route path="dashboard" element={<AdminDashboardPage />} />
      <Route path="domains" element={<DomainsPage />} />
      <Route path="dns" element={<DNSPage />} />
      <Route path="mailbox-requests" element={<MailboxRequestsPage />} />
      <Route path="users" element={<UsersPage />} />
      <Route path="invitations" element={<InvitationsPage />} />
      <Route path="sso" element={<SSOPage />} />
      <Route path="queue" element={<QueuePage />} />
      <Route path="spam" element={<SpamPage />} />
      <Route path="system-health" element={<SystemHealthPage />} />
      <Route path="backup" element={<BackupPage />} />
      <Route path="deploy-guide" element={<DeployGuidePage />} />
      <Route path="logs" element={<LogsPage />} />
      <Route path="settings" element={<AdminSettingsPage />} />
    </Route>
    <Route path="/user" element={<RequireAuth audience="user"><AppShell audience="user" items={userNav} /></RequireAuth>}>
      <Route index element={<Navigate to="dashboard" replace />} />
      <Route path="dashboard" element={<UserDashboardPage />} />
      <Route path="mailbox-request" element={<MailboxRequestPage />} />
      <Route path="mail" element={<MailLayout />}>
        <Route index element={<MailboxPage />} />
        <Route path="sent" element={<SentPage />} />
        <Route path="drafts" element={<FolderPage folder="drafts" title="user.drafts" />} />
        <Route path="spam" element={<FolderPage folder="spam" title="user.spam" />} />
        <Route path="trash" element={<FolderPage folder="trash" title="user.trash" />} />
      </Route>
      <Route path="compose" element={<ComposePage />} />
      <Route path="contacts" element={<ContactsPage />} />
      <Route path="settings" element={<UserSettingsPage />} />
    </Route>
    <Route path="*" element={<Navigate to="/" replace />} />
  </Routes>
}
